import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { SectionCard, Mono, InfoNote, ProvenanceBadge } from "@/components/uikit";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

// Live GitHub source-state retrieval + deterministic SHA-256 source correlation.
// CONNECTED = GitHub retrieval succeeded. This panel makes NO claim of RFC 3161,
// blockchain, government, or third-party attestation.
export default function LiveSourceProvenance() {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sourceChanged, setSourceChanged] = useState(false);
  const prevCommit = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/signallink/source`, {
        method: "GET",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      if (!res.ok || data.status === "ERROR") {
        throw new Error(data.error || `Source verification failed (${res.status})`);
      }
      const cur = data.source?.commit_sha ?? null;
      setSourceChanged(!!(prevCommit.current && cur && prevCommit.current !== cur));
      if (cur) prevCommit.current = cur;
      setReceipt(data);
    } catch (e) {
      setReceipt({ status: "ERROR", error: e instanceof Error ? e.message : "Source verification failed" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Two-minute refresh keeps the display fresh without exhausting the
    // unauthenticated GitHub request budget.
    const t = window.setInterval(() => void refresh(), 120000);
    return () => window.clearInterval(t);
  }, [refresh]);

  const connected = receipt?.status === "CONNECTED" && Boolean(receipt?.source?.commit_sha);

  return (
    <SectionCard
      title="Live Source Provenance · GitHub → SignalLink → Emergent"
      icon="GitBranch"
      testId="live-source-provenance"
      right={
        <Button
          size="sm"
          onClick={() => void refresh()}
          disabled={loading}
          className="h-8 bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400"
          data-testid="lsp-verify-button"
        >
          {loading ? <Icons.Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Icons.RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
          {loading ? "VERIFYING…" : "VERIFY SOURCE NOW"}
        </Button>
      }
    >
      {sourceChanged && (
        <InfoNote tone="warn" className="mb-3" data-testid="lsp-source-changed">
          SOURCE VERSION CHANGED — a new GitHub commit was detected.
        </InfoNote>
      )}

      {!receipt && <p className="py-4 text-sm text-slate-500" data-testid="lsp-waiting">Waiting for source verification…</p>}

      {receipt?.status === "ERROR" && (
        <InfoNote tone="danger" data-testid="lsp-error">SOURCE ERROR: {receipt.error || "Unknown error"}</InfoNote>
      )}

      {connected && receipt?.source && (
        <div className="space-y-2" data-testid="lsp-connected">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded border border-emerald-600 bg-emerald-950 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-300" data-testid="lsp-status">
              <Icons.PlugZap className="h-3 w-3" aria-hidden="true" /> CONNECTED
            </span>
            <ProvenanceBadge source="local" />
          </div>
          <Row label="Repository" value={receipt.source.repository} testId="lsp-repo" />
          <Row label="Branch" value={receipt.source.branch} testId="lsp-branch" />
          <Row label="Commit" value={receipt.source.commit_sha.slice(0, 16) + "…"} href={receipt.source.commit_url} testId="lsp-commit" />
          <Row label="Commit time" value={receipt.source.commit_timestamp ? new Date(receipt.source.commit_timestamp).toLocaleString() : "Unknown"} />
          <Row label="Author" value={receipt.source.author || "Unknown"} />
          <Row label="Runtime" value="Emergent" />
          <Row label="Source SHA-256" value={receipt.source_sha256 || "—"} mono testId="lsp-source-sha" />
          <Row label="Observed" value={receipt.observed_at ? new Date(receipt.observed_at).toLocaleString() : "Unknown"} />
          <p className="mt-1 border-t border-slate-800 pt-2 text-[11px] leading-relaxed text-slate-500">
            Local SHA-256 source correlation only. No RFC 3161, blockchain, government, or third-party attestation is claimed by this panel.
          </p>
        </div>
      )}
    </SectionCard>
  );
}

function Row({ label, value, href, mono, testId }) {
  return (
    <div className="grid grid-cols-[minmax(120px,0.6fr)_1.4fr] gap-3 border-b border-slate-900 pb-1.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="break-all font-mono text-xs text-cyan-300 hover:text-cyan-200" data-testid={testId}>{value}</a>
      ) : mono ? (
        <Mono testId={testId}>{value}</Mono>
      ) : (
        <span className="break-all text-xs text-slate-200" data-testid={testId}>{value}</span>
      )}
    </div>
  );
}
